import React from 'react';
import './CreatureDetail.css';
import bg from './img/background/background.jpg';
import blathersImg from './img/blathers.png';
import { getCreatureInfo, isAvailableInMonth } from './creatureDatabase';
import { fishData } from './fishData';
import { bugData } from './bugData';
import { seaData } from './seaData';

function CreatureDetail({ creature, onClose, category, isCollected, onToggleCollection }) {
  if (!creature) return null;

  const overlayStyle = {
    backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url(${bg})`,
    backgroundRepeat: 'repeat',
    backgroundSize: '500px',
    backgroundPosition: 'left top'
  };

  // Get creature type based on category
  const getCreatureType = () => {
    switch(category) {
      case 'fish': return 'Fish';
      case 'bug': return 'Bug';
      case 'sea': return 'Sea Creature';
      default: return 'Creature';
    }
  };

  // Get creature info from database
  const info = getCreatureInfo(creature.name, category);

  // Function to get creature ID based on category and position
  const getCreatureId = () => {
    if (category === 'fish') {
      const index = fishData.findIndex(fish => fish.name === creature.name);
      return index !== -1 ? index + 1 : 1; // Fish: 1-80
    } else if (category === 'bug') {
      const index = bugData.findIndex(bug => bug.name === creature.name);
      return index !== -1 ? index + 1 : 1; // Bugs: 1-80
    } else if (category === 'sea') {
      const index = seaData.findIndex(sea => sea.name === creature.name);
      return index !== -1 ? index + 1 : 1; // Sea: 1-40
    }
    return 1; // Default
  };

  const creatureId = getCreatureId();

  // Function to format numbers with commas
  const formatPrice = (price) => {
    return price.toLocaleString();
  };

  // Function to get rarity class and stars
  const getRarityDisplay = (rarity) => {
    switch(rarity.toLowerCase()) {
      case 'common':
        return { class: 'common', stars: '★' };
      case 'uncommon':
        return { class: 'uncommon', stars: '★★' };
      case 'rare':
        return { class: 'rare', stars: '★★★' };
      default:
        return { class: 'common', stars: '★' };
    }
  };

  const rarityDisplay = getRarityDisplay(info.rarity);

  return (
    <div className="creature-detail-overlay" style={overlayStyle}>
      <div className="creature-detail-container">
        {/* Header */}
        <div className="creature-detail-header">
          <button className="back-button" onClick={onClose}>
            ← Back
          </button>
          <div className="header-title">
            <img src={blathersImg} alt="Blathers" className="blathers-logo" />
            <h1 className="app-title">Blathers Corner</h1>
          </div>

        </div>

        {/* Main Content */}
        <div className="creature-detail-content">
          {/* Left Side - Image and Basic Info */}
          <div className="creature-detail-left">
            <div className="creature-number-name">
              <h2>{creatureId}. {creature.name}</h2>
            </div>
            
            <div className="creature-image-container">
              <img 
                src={creature.image} 
                alt={creature.name}
                className="creature-detail-image"
              />
            </div>

            {category !== 'sea' && (
              <div className="rarity-section">
                <span className={`rarity-badge ${rarityDisplay.class}`}>
                  {info.rarity} {rarityDisplay.stars}
                </span>
              </div>
            )}

            <div className="description-section">
              <p className="creature-description" style={{whiteSpace: 'pre-line'}}>{info.description}</p>
            </div>

            <button 
              className={`mark-caught-button ${isCollected ? 'caught' : 'not-caught'}`}
              onClick={onToggleCollection}
            >
              {isCollected ? 'Caught' : 'Not Caught'}
            </button>
          </div>

          {/* Right Side - Detailed Info */}
          <div className="creature-detail-right">
            {/* Months Calendar */}
            <div className="months-section">
              <div className="hemispheres-container">
                {/* North Hemisphere */}
                <div className="hemisphere-calendar">
                  <div className="hemisphere-label">North</div>
                  <div className="months-grid">
                    {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((month, index) => (
                      <div key={`north-${month}`} className={`month-button ${isAvailableInMonth(creature.name, index, 'north') ? 'available' : ''}`}>
                        {month}
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* South Hemisphere */}
                <div className="hemisphere-calendar">
                  <div className="hemisphere-label">South</div>
                  <div className="months-grid">
                    {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((month, index) => (
                      <div key={`south-${month}`} className={`month-button ${isAvailableInMonth(creature.name, index, 'south') ? 'available' : ''}`}>
                        {month}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Availability Times */}
            <div className="availability-section">
              <h3>Availability</h3>
              <div style={{whiteSpace: 'pre-line'}}>
                {info.timeAvailable.split('\n').map((line, index) => {
                  const lines = info.timeAvailable.split('\n');
                  const isMultiLine = lines.length > 1;
                  const isSeasonalInfo = isMultiLine && (index === 0 || line.includes('(North)') || line.includes('(South)'));
                  return (
                    <div key={index} style={{
                      fontSize: isSeasonalInfo ? '0.8em' : '1em',
                      color: isSeasonalInfo ? '#666' : 'inherit'
                    }}>
                      {line}
                    </div>
                  );
                })}
              </div>
              
              <div className="time-chart">
                <div className="time-labels-row">
                  <span>12 AM</span>
                  <span>6 AM</span>       
                </div>
                <div className="time-bar has-middle-notch">
                  {Array.from({length: 12}, (_, i) => (
                    <div 
                      key={i}
                      className={`time-segment ${info.availableHours.includes(i) ? 'available' : 'unavailable'}`}
                    />
                  ))}
                </div>
                <div className="time-labels-row">
                  <span>12 PM</span>
                  <span>6 PM</span>
                </div>
                <div className="time-bar has-middle-notch">
                  {Array.from({length: 12}, (_, i) => (
                    <div 
                      key={i + 12}
                      className={`time-segment ${info.availableHours.includes(i + 12) ? 'available' : 'unavailable'}`}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Additional Info */}
            <div className="additional-info">
              {category === 'fish' && (
                <div className="info-item">
                  <strong>Location:</strong> {info.location}
                </div>
              )}
              {category === 'bug' && (
                <div className="info-item">
                  <strong>Location:</strong> {info.location}
                </div>
              )}
              {(category === 'fish' || category === 'sea') && (
                <div className="info-item">
                  <strong>Shadow:</strong> {info.shadow}
                </div>
              )}
              {category === 'sea' && info.movement && (
                <div className="info-item">
                  <strong>Movement:</strong> {info.movement}
                </div>
              )}
              {category === 'bug' && info.weather && (
                <div className="info-item">
                  <strong>Weather:</strong> {info.weather}
                </div>
              )}
              {info.spawnRequirements && (
                <div className="info-item">
                  <strong>Spawn Requirements:</strong> {info.spawnRequirements}
                </div>
              )}
              <div className="info-item">
                <strong>Nook Price:</strong> {formatPrice(info.price)} Bells
              </div>
              {info.cjPrice && (
                <div className="info-item">
                  <strong>C.J. Price:</strong> {formatPrice(info.cjPrice)} Bells
                </div>
              )}
              {info.flickPrice && (
                <div className="info-item">
                  <strong>Flick Price:</strong> {formatPrice(info.flickPrice)} Bells
                </div>
              )}
              {info.notes && (
                <div className="info-item notes">
                  <strong>Notes:</strong> {info.notes}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreatureDetail;